/**
 * Paywall Component
 * Shows subscription options when user tries to access a premium feature
 */
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Crown, Zap, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import subscriptionService from '../services/subscriptionService';
import { Capacitor } from '@capacitor/core';
import { openInAppBrowser } from '../utils/capacitor';
import { PRIVACY_URL, TERMS_URL } from '../config/legalUrls';


// Customer-facing Fitxess Pro benefits. AI photo logging is now a public
// Fitxess Pro feature (broad release) and is listed here.
const PRO_BENEFITS = [
  'Personalized meal plans for your entire week',
  'Optional leftovers built into your plan',
  'Grocery lists generated from your meal plan',
  'AI photo logging for faster meal tracking',
  'Faster logging with barcodes & restaurant menus',
  'Unlimited replanning & custom macro targets',
];

// Parse a free-trial period (iOS introPrice shape OR Android Period / ISO8601)
// into { value, unit }. Returns null when there's no usable period.
function parseFreePeriod(period) {
  if (!period) return null;
  let value = null;
  let unit = null;
  if (typeof period === 'string') {
    const m = period.match(/P(\d+)([DWMY])/i);
    if (m) {
      value = parseInt(m[1], 10);
      unit = { D: 'day', W: 'week', M: 'month', Y: 'year' }[m[2].toUpperCase()];
    }
  } else if (typeof period === 'object') {
    // iOS introPrice: { periodNumberOfUnits, periodUnit } | Android Period: { value, unit, iso8601 }
    if (period.iso8601) return parseFreePeriod(period.iso8601);
    value = period.periodNumberOfUnits ?? period.value ?? null;
    const rawUnit = (period.periodUnit ?? period.unit ?? '').toString().toLowerCase();
    if (rawUnit.startsWith('day')) unit = 'day';
    else if (rawUnit.startsWith('week')) unit = 'week';
    else if (rawUnit.startsWith('month')) unit = 'month';
    else if (rawUnit.startsWith('year')) unit = 'year';
  }
  if (!value || !unit) return null;
  return { value, unit };
}

function freePeriodText(p) {
  return p ? `${p.value} ${p.unit}${p.value > 1 ? 's' : ''} free` : null;
}

function trialCtaText(p) {
  if (!p) return 'Continue';
  const u = p.unit.charAt(0).toUpperCase() + p.unit.slice(1);
  return `Start ${p.value}-${u} Free Trial`;
}

function Paywall({ isOpen, onClose, feature, onPurchaseComplete }) {
  // Consume the shared SubscriptionContext (NOT a fresh useSubscription()
  // instance) so any entitlement updates from here flow to every other
  // screen — Grocery, Home, PlanningHub, Settings — instead of staying
  // trapped in a second local hook instance.
  const {
    offerings,
    offeringsLoading,
    offeringsError,
    reloadOfferings,
    purchase,
    restore,
    loading,
    isSubscribed,
    isRecovering,
    syncCustomerInfo,
    rcReady,
    userId,
  } = useSubscriptionContext();
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('annual');
  const [eligibility, setEligibility] = useState({});
  // Tracks whether the active purchase flow has already been resolved
  // (success / failure / "already-subscribed" recovery) so the watchdog
  // and any late-arriving promise resolution don't double-fire UI updates.
  const purchaseSettledRef = useRef(false);
  const watchdogRef = useRef(null);

  // If RevenueCat's customerInfo listener (or any other source) flips the
  // user to subscribed while the paywall is open — including the iOS
  // "already subscribed" recovery path where the purchase() promise hangs
  // — clear the spinner and close the paywall immediately.
  //
  // The "Welcome to Fitxess Pro!" celebration only fires when the user
  // was actively in a purchase or restore flow at the time the entitlement
  // resolved. If the paywall happened to be open passively (e.g., the
  // global 402 interceptor opened it while a background reconcile was
  // in-flight) and an unrelated customerInfo update flips isSubscribed to
  // true, we close silently — no toast, no repeated celebration on every
  // navigation.
  useEffect(() => {
    if (!isOpen) return;
    if (!isSubscribed) return;
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
    if (!purchaseSettledRef.current) {
      purchaseSettledRef.current = true;
      setPurchasing(false);
      if (purchasing || restoring) {
        toast.success('Welcome to Fitxess Pro!');
      }
      onPurchaseComplete?.();
      onClose();
    }
  }, [isOpen, isSubscribed, purchasing, restoring, onClose, onPurchaseComplete]);

  // Reset settle guard when paywall reopens. Also reset the selected plan to
  // Annual on every open so a previous Monthly selection never persists.
  useEffect(() => {
    if (isOpen) {
      purchaseSettledRef.current = false;
      setSelectedPackage('annual');
    }
    return () => {
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
      }
    };
  }, [isOpen]);

  // Get packages from offerings
  const packages = offerings?.current?.availablePackages || [];

  // Robust package matcher. RevenueCat offerings can label packages in several
  // ways depending on how the Offering was configured:
  //   - Standard types: `$rc_monthly`, `$rc_annual`
  //   - Custom labels:  `monthly`, `annual`, `yearly`
  //   - Package identifier may not match the App Store Connect product ID
  // We also fall back to a case-insensitive substring match on the underlying
  // product identifier so product IDs like `Fitxess_Pro_Annual` still resolve
  // to the annual slot.
  const findPackage = (type) => {
    const aliases = type === 'annual' ? ['annual', 'yearly'] : [type];
    return packages.find((p) => {
      const pkgId = (p.identifier || '').toLowerCase();
      const prodId = (p.product?.identifier || '').toLowerCase();
      return aliases.some(
        (a) => pkgId === a || pkgId === `$rc_${a}` || prodId.includes(a)
      );
    });
  };
  const monthlyPackage = findPackage('monthly');
  const annualPackage = findPackage('annual');

  // Prices come ONLY from live RevenueCat offerings — never a hard-coded
  // fallback. If they aren't available we show a loading/error state instead.
  const hasPrices = !!(monthlyPackage?.product?.priceString && annualPackage?.product?.priceString);
  const isNative = Capacitor.isNativePlatform();
  const showLoadingPrices = isNative && (offeringsLoading || (!hasPrices && !offeringsError));
  const showPricesError = isNative && !offeringsLoading && (offeringsError || !hasPrices);

  // Savings badge only when BOTH real prices are present.
  const monthlyCost = monthlyPackage?.product?.price;
  const annualCost = annualPackage?.product?.price;
  const savingsPercent = (monthlyCost && annualCost)
    ? Math.round((1 - annualCost / (monthlyCost * 12)) * 100)
    : 0;

  // Platform-specific store wording for the purchase disclosure footer.
  // platform can be overridden in web preview via window.__FITXESS_PLATFORM_SIM__
  // purely for screenshots — never set on a real device.
  const platform = (typeof window !== 'undefined' && window.__FITXESS_PLATFORM_SIM__) || Capacitor.getPlatform();
  const isAndroid = platform === 'android';
  const storeAccount = isAndroid ? 'Google Play' : 'Apple ID';
  const storeSettings = isAndroid ? 'Google Play subscription settings' : 'App Store subscription settings';

  // Fetch introductory-offer (free-trial) eligibility for the two products.
  // Eligibility is customer-specific, so we CLEAR it immediately whenever the
  // paywall opens or the authenticated user changes — this guarantees we never
  // briefly render a previous user's trial state — then load fresh values.
  // iOS returns a real per-product status; Android returns UNKNOWN and we
  // present the offer when the product exposes a free phase. On web this is
  // driven by window.__FITXESS_TRIAL_SIM__ for preview.
  useEffect(() => {
    setEligibility({});
    if (!isOpen) return;
    const ids = [monthlyPackage?.product?.identifier, annualPackage?.product?.identifier].filter(Boolean);
    if (ids.length === 0) return;
    let cancelled = false;
    subscriptionService.getIntroEligibility(ids)
      .then((map) => { if (!cancelled) setEligibility(map || {}); })
      .catch(() => { if (!cancelled) setEligibility({}); });
    return () => { cancelled = true; };
  }, [isOpen, userId, monthlyPackage?.product?.identifier, annualPackage?.product?.identifier]);

  // Resolve a package's trial: whether THIS customer sees a free trial, the
  // free-period label, and the recurring "then" price. Trial data comes ONLY
  // from live store/RC data (or the web preview simulation) — never assumed.
  const webSim = (typeof window !== 'undefined') ? window.__FITXESS_TRIAL_SIM__ : null;
  const getTrial = (pkg) => {
    const product = pkg?.product;
    if (!product) return { eligible: false, period: null, thenPrice: null };
    const introFreePeriod = (product.introPrice && Number(product.introPrice.price) === 0)
      ? parseFreePeriod(product.introPrice) : null;
    let period = null;
    let eligible = false;
    if (platform === 'android') {
      const opt = product.defaultOption || (product.subscriptionOptions || [])[0];
      period = parseFreePeriod(opt?.freePhase?.billingPeriod) || introFreePeriod;
      // Google determines actual eligibility at purchase; present the offer
      // whenever it exists. (web preview can force ineligible via webSim.)
      eligible = !!period && webSim !== 'ineligible';
    } else {
      // iOS/web: status 2 = ELIGIBLE. Unknown/ineligible => no trial claim.
      period = introFreePeriod;
      eligible = !!period && eligibility[product.identifier]?.status === 2;
    }
    return { eligible, period: eligible ? period : null, thenPrice: product.priceString };
  };
  const monthlyTrial = getTrial(monthlyPackage);
  const annualTrial = getTrial(annualPackage);
  const selectedTrial = selectedPackage === 'annual' ? annualTrial : monthlyTrial;
  const ctaLabel = selectedTrial.eligible ? trialCtaText(selectedTrial.period) : 'Continue';
  // Renewal disclosure summarizing the SELECTED plan.
  const selectedPriceString = (selectedPackage === 'annual' ? annualPackage : monthlyPackage)?.product?.priceString;
  const selectedPer = selectedPackage === 'annual' ? 'year' : 'month';
  const renewalSummary = selectedTrial.eligible
    ? `${freePeriodText(selectedTrial.period)}, then ${selectedPriceString}/${selectedPer}. Auto-renews unless canceled.`
    : `${selectedPriceString}/${selectedPer}. Auto-renews unless canceled.`;

  const handlePurchase = async () => {
    const packageToPurchase = selectedPackage === 'annual' ? annualPackage : monthlyPackage;

    if (!packageToPurchase) {
      toast.error('Package not available');
      return;
    }

    if (!Capacitor.isNativePlatform()) {
      toast.error('Subscriptions are only available in the app');
      return;
    }

    purchaseSettledRef.current = false;
    setPurchasing(true);

    // Watchdog: iOS sometimes shows "You're currently subscribed to this"
    // and never delivers a transaction event back to RevenueCat, leaving
    // purchase() hanging. After a short grace period, refresh customerInfo
    // ourselves and unlock if the entitlement is already active.
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    watchdogRef.current = setTimeout(async () => {
      if (purchaseSettledRef.current) return;
      try {
        const { applied } = await syncCustomerInfo();
        if (applied && !purchaseSettledRef.current) {
          purchaseSettledRef.current = true;
          setPurchasing(false);
          toast.success('Welcome to Fitxess Pro!');
          onPurchaseComplete?.();
          onClose();
        }
      } catch (e) {
        console.warn('[Paywall] watchdog sync failed', e);
      }
    }, 3000);

    try {
      const result = await purchase(packageToPurchase);
      const applied = result?.applied === true;
      if (!purchaseSettledRef.current) {
        if (applied) {
          purchaseSettledRef.current = true;
          toast.success('Welcome to Fitxess Pro!');
          onPurchaseComplete?.();
          onClose();
        } else {
          // StoreKit succeeded but RevenueCat hasn't surfaced an active
          // entitlement yet. Keep the paywall open in a "finishing up"
          // state; the customerInfo listener + watchdog + isSubscribed
          // watcher will close the paywall once RC catches up.
          toast.message('Finishing up your subscription…');
          // Do not set purchaseSettledRef; do not close.
        }
      }
    } catch (error) {
      if (purchaseSettledRef.current) return;
      purchaseSettledRef.current = true;
      if (error?.message === 'Purchase cancelled' || error?.userCancelled) {
        // User backed out — silent.
      } else {
        toast.error('Purchase failed. Please try again.');
      }
    } finally {
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
      }
      // Only stop the spinner if the purchase is fully settled. If we're
      // still waiting for RC to confirm entitlement ("Finishing up…"), keep
      // the spinner so the user sees continued progress until the listener
      // or watchdog resolves it.
      if (purchaseSettledRef.current) {
        setPurchasing(false);
      }
    }
  };

  const handleRestore = async () => {
    try {
      setRestoring(true);
      const result = await restore();
      if (result?.applied) {
        toast.success('Purchases restored');
        onClose();
      } else {
        toast.message('No active subscription found to restore');
      }
    } catch (error) {
      toast.error('Failed to restore purchases');
    } finally {
      setRestoring(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end justify-center sm:items-center"
        onClick={onClose}
        data-testid="paywall-overlay"
      >
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative bg-background rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 pb-8 shadow-xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="paywall-modal"
        >
          {/* Close button — inside the modal so it stays pinned to the modal
              corner (not the viewport) and sits above the scrollable content
              so taps aren't swallowed by the content layer. */}
          <button
            onClick={onClose}
            type="button"
            aria-label="Close"
            className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center bg-secondary/60 text-foreground hover:bg-secondary active:scale-95 transition"
            data-testid="paywall-close-btn"
          >
            <X size={20} />
          </button>

          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-accent to-cyan-400 flex items-center justify-center">
              <Crown size={32} className="text-white" />
            </div>
            <h2 className="font-heading font-bold text-2xl mb-2">
              Upgrade to Fitxess Pro
            </h2>
          </div>

          {/* Features list */}
          <div className="bg-secondary/30 rounded-xl p-4 mb-6">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3">
              KEY FEATURES INCLUDE:
            </p>
            <ul className="space-y-2">
              {PRO_BENEFITS.map((benefit, i) => (
                <li key={i} className="flex items-center gap-2 text-sm" data-testid={`paywall-benefit-${i}`}>
                  <Check size={16} className="text-accent flex-shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Prices error state — offerings failed to load. No stale prices;
              offer a Retry and keep Restore Purchases available. */}
          {showPricesError ? (
            <div className="rounded-xl border-2 border-border/40 p-5 mb-4 text-center" data-testid="paywall-offerings-error">
              <AlertCircle size={28} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm font-medium mb-1">We couldn't load subscription prices</p>
              <p className="text-xs text-muted-foreground mb-4">
                Check your connection and try again.
              </p>
              <Button
                onClick={reloadOfferings}
                disabled={offeringsLoading}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-accent to-cyan-500 text-white font-semibold"
                data-testid="paywall-retry-btn"
              >
                <span className="flex items-center gap-2">
                  <RefreshCw size={18} className={offeringsLoading ? 'animate-spin' : ''} />
                  {offeringsLoading ? 'Loading…' : 'Retry'}
                </span>
              </Button>
            </div>
          ) : showLoadingPrices ? (
            /* Loading prices skeleton */
            <div className="space-y-3 mb-6" data-testid="paywall-offerings-loading">
              {[0, 1].map((i) => (
                <div key={i} className="w-full p-4 rounded-xl border-2 border-border/40 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <div className="h-4 w-20 bg-secondary rounded" />
                      <div className="h-3 w-16 bg-secondary/70 rounded" />
                    </div>
                    <div className="h-5 w-16 bg-secondary rounded" />
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground pt-1">
                <span className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                Loading prices…
              </div>
            </div>
          ) : (
            <>
              {/* Plan selection */}
              <div className="space-y-3 mb-6">
                {annualPackage && (
                  <button
                    onClick={() => setSelectedPackage('annual')}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left relative ${
                      selectedPackage === 'annual'
                        ? 'border-accent bg-accent/5'
                        : 'border-border/40 hover:border-border'
                    }`}
                    data-testid="plan-annual"
                  >
                    {savingsPercent > 0 && (
                      <span className="absolute -top-2 right-4 px-2 py-0.5 bg-accent text-white text-xs font-semibold rounded-full">
                        Save {savingsPercent}%
                      </span>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">Annual</p>
                        <p className="text-sm text-muted-foreground">Best value</p>
                      </div>
                      <div className="text-right">
                        {annualTrial.eligible ? (
                          <>
                            <p className="font-bold text-base text-accent" data-testid="plan-annual-trial">
                              {freePeriodText(annualTrial.period)}
                            </p>
                            <p className="text-xs text-muted-foreground" data-testid="plan-annual-price">
                              then {annualPackage?.product?.priceString}/year
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="font-bold text-lg" data-testid="plan-annual-price">
                              {annualPackage?.product?.priceString}
                            </p>
                            <p className="text-xs text-muted-foreground">per year</p>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                )}

                {monthlyPackage && (
                  <button
                    onClick={() => setSelectedPackage('monthly')}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      selectedPackage === 'monthly'
                        ? 'border-accent bg-accent/5'
                        : 'border-border/40 hover:border-border'
                    }`}
                    data-testid="plan-monthly"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">Monthly</p>
                        <p className="text-sm text-muted-foreground">Flexible</p>
                      </div>
                      <div className="text-right">
                        {monthlyTrial.eligible ? (
                          <>
                            <p className="font-bold text-base text-accent" data-testid="plan-monthly-trial">
                              {freePeriodText(monthlyTrial.period)}
                            </p>
                            <p className="text-xs text-muted-foreground" data-testid="plan-monthly-price">
                              then {monthlyPackage?.product?.priceString}/month
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="font-bold text-lg" data-testid="plan-monthly-price">
                              {monthlyPackage?.product?.priceString}
                            </p>
                            <p className="text-xs text-muted-foreground">per month</p>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                )}
              </div>

              {/* Selected-plan renewal disclosure */}
              <p className="text-xs text-center text-muted-foreground mb-3" data-testid="paywall-renewal-summary">
                {renewalSummary}
              </p>

              {/* Purchase button */}
              <Button
                onClick={handlePurchase}
                disabled={purchasing || isRecovering || loading || !rcReady}
                className="w-full h-14 rounded-xl bg-gradient-to-r from-accent to-cyan-500 text-white font-semibold text-lg hover:opacity-90"
                data-testid="purchase-btn"
              >
                {isRecovering ? (
                  <span
                    className="flex items-center gap-2"
                    data-testid="paywall-confirming-label"
                  >
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Confirming your Fitxess Pro access…
                  </span>
                ) : purchasing ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing…
                  </span>
                ) : !rcReady && Capacitor.isNativePlatform() ? (
                  <span
                    className="flex items-center gap-2"
                    data-testid="paywall-preparing-label"
                  >
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Preparing subscription…
                  </span>
                ) : (
                  <span className="flex items-center gap-2" data-testid="purchase-cta-label">
                    <Zap size={20} />
                    {ctaLabel}
                  </span>
                )}
              </Button>
            </>
          )}

          {/* Restore purchases */}
          <button
            onClick={handleRestore}
            disabled={restoring}
            className="w-full mt-3 text-sm text-muted-foreground hover:text-foreground"
            data-testid="restore-btn"
          >
            {restoring ? 'Restoring...' : 'Restore Purchases'}
          </button>

          {/* Legal text — conditional on this customer's trial eligibility and
              platform store wording. Eligible: no charge today; the plan begins
              automatically after the free trial unless canceled. Ineligible:
              payment is charged at confirmation. */}
          <p className="text-[10px] text-muted-foreground text-center mt-4 leading-relaxed" data-testid="paywall-legal-disclosure">
            {selectedTrial.eligible ? (
              <>
                You won't be charged today. Your {selectedPer}ly subscription begins
                automatically after your {freePeriodText(selectedTrial.period)} trial
                unless you cancel at least 24 hours before it ends. Payment is then
                charged to your {storeAccount} account and renews each {selectedPer}
                until canceled. Manage or cancel anytime in your {storeSettings}.
              </>
            ) : (
              <>
                Payment will be charged to your {storeAccount} account at confirmation of purchase.
                Subscription automatically renews unless canceled at least 24 hours before the
                end of the current period. Manage or cancel anytime in your {storeSettings}.
              </>
            )}
          </p>

          {/* Legal links */}
          <p className="text-[11px] text-muted-foreground text-center mt-2">
            <a
              href={TERMS_URL}
              onClick={(e) => { e.preventDefault(); openInAppBrowser(TERMS_URL); }}
              className="underline hover:text-foreground cursor-pointer"
              data-testid="paywall-terms-link"
            >
              Terms of Use
            </a>
            <span className="mx-2" aria-hidden="true">·</span>
            <a
              href={PRIVACY_URL}
              onClick={(e) => { e.preventDefault(); openInAppBrowser(PRIVACY_URL); }}
              className="underline hover:text-foreground cursor-pointer"
              data-testid="paywall-privacy-link"
            >
              Privacy Policy
            </a>
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default Paywall;
